import { lazy } from 'react'

import { Route, Routes } from 'react-router'
import {
  AccountantsUrl,
  ComplianceForm,
  ComplianceOverviewUrl,
  ComplianceUrl,
  DashboardUrl,
  DataExportsUrl,
  MvrOverviewUrl,
  PremiumUrl,
  ReportDetailUrl,
  ReportsUrl,
  RoadSmartUrl,
  TaxInfoUrl,
  TeamsFeatureUrl,
  TransactionsUrl,
  TripsUrl
} from 'constants/RouteUrls'
import NotFoundPage from 'routes/NotFoundPage'
import { lazyLoadHelper } from 'routes/resources'
import Suspense from 'core/components/suspense/Suspense'
import { useSettings } from 'core/hooks/settings/useSettings'

const HomePage = lazy(() =>
  lazyLoadHelper(() => import('pages/dashboard/home/HomePage'))
)
const TripsPage = lazy(() =>
  lazyLoadHelper(() => import('pages/dashboard/trips/TripsPage'))
)
const TransactionsPage = lazy(() =>
  lazyLoadHelper(() => import('pages/dashboard/transactions/TransactionsPage'))
)
const ReportsPage = lazy(() =>
  lazyLoadHelper(() => import('pages/dashboard/reports/ReportsPage'))
)
const DataExportPage = lazy(() =>
  lazyLoadHelper(() => import('pages/shared/data-export/DataExportPage'))
)
const TaxInfoPage = lazy(() =>
  lazyLoadHelper(() => import('pages/dashboard/tax-info/TaxInfoPage'))
)
const TeamsFeaturesPage = lazy(() =>
  lazyLoadHelper(
    () => import('pages/dashboard/teams-features/TeamsFeaturesPage')
  )
)
const PremiumPage = lazy(() =>
  lazyLoadHelper(() => import('pages/shared/premium/PremiumPage'))
)
const ComplianceOverview = lazy(() =>
  lazyLoadHelper(
    () => import('pages/dashboard/compliance/overview/ComplianceOverview')
  )
)
const DriverFormPage = lazy(() =>
  lazyLoadHelper(
    () => import('pages/dashboard/compliance/driver-form/DriverFormPage')
  )
)
const AccountantsPage = lazy(() =>
  lazyLoadHelper(() => import('pages/dashboard/accountants/AccountantsPage'))
)
const ReportPageNew = lazy(() =>
  lazyLoadHelper(() => import('pages/shared/report/ReportPage'))
)
const PurpleReportPage = lazy(() =>
  lazyLoadHelper(() => import('pages/shared/report/purple/PurpleReportPage'))
)

const MotusPageRedirect = lazy(() =>
  lazyLoadHelper(() => import('core/widgets/MotusPageRedirect'))
)

const RoadSmartPage = lazy(() =>
  lazyLoadHelper(() => import('pages/dashboard/road-smart/RoadSmartPage'))
)

const PersonalDashboardRoutes = () => {
  const {
    data: {
      featureFlags: { ENABLE_DRIVER_EXPERIENCE }
    }
  } = useSettings()
  return (
    <Routes>
      <Route
        path={DashboardUrl}
        element={
          <Suspense variant="home">
            <HomePage />
          </Suspense>
        }
      />
      <Route
        path={TripsUrl}
        element={
          <Suspense variant="default" withBanner>
            <TripsPage />
          </Suspense>
        }
      />
      <Route
        path={TransactionsUrl}
        element={
          <Suspense variant="default" withBanner>
            <TransactionsPage />
          </Suspense>
        }
      />
      <Route
        path={ReportsUrl}
        element={
          <Suspense variant="default">
            <ReportsPage />
          </Suspense>
        }
      />
      <Route
        path={DataExportsUrl}
        element={
          <Suspense variant="default">
            <DataExportPage />
          </Suspense>
        }
      />
      <Route
        path={ComplianceOverviewUrl}
        element={
          <Suspense variant="default">
            <MotusPageRedirect
              line1="A separated window will open for you to submit"
              line2="your compliance information"
              path={ComplianceForm}
              buttonText="Go to Compliance"
            >
              <ComplianceOverview />
            </MotusPageRedirect>
          </Suspense>
        }
      />
      <Route
        path={MvrOverviewUrl}
        element={
          <Suspense variant="default">
            <MotusPageRedirect
              line1="A separated window will open for you to submit"
              line2="your mvr information"
              path={ComplianceForm}
              buttonText="Go to MVR"
            />
          </Suspense>
        }
      />

      <Route
        path={TaxInfoUrl}
        element={
          <Suspense variant="default">
            <TaxInfoPage />
          </Suspense>
        }
      />

      <Route
        path={ComplianceUrl}
        element={
          <Suspense variant="default">
            <DriverFormPage />
          </Suspense>
        }
      />
      {ENABLE_DRIVER_EXPERIENCE && (
        <Route
          path={RoadSmartUrl}
          element={
            <Suspense variant="default">
              <RoadSmartPage />
            </Suspense>
          }
        />
      )}
      <Route
        path={AccountantsUrl}
        element={
          <Suspense variant="default">
            <AccountantsPage />
          </Suspense>
        }
      />
      <Route
        path={TeamsFeatureUrl}
        element={
          <Suspense variant="teams-features">
            <TeamsFeaturesPage />
          </Suspense>
        }
      />

      {/*This url will show the placeholder data, plans, FAQ, etc*/}
      <Route
        path={PremiumUrl}
        element={
          <Suspense variant="premium">
            <PremiumPage />
          </Suspense>
        }
      />

      {/*Excluded from side menu*/}
      <Route
        path={ReportDetailUrl}
        element={
          <Suspense variant="report" isChildPage>
            {ENABLE_DRIVER_EXPERIENCE ? (
              <PurpleReportPage />
            ) : (
              <ReportPageNew />
            )}
          </Suspense>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default PersonalDashboardRoutes
